/**
 * Das Gespraech als Mail-Fenster (Mockup V4, freigegeben 21.10.2026,
 * docs/KONZEPT-GESPRAECH-MAILFENSTER.md): drei Reiter Gast · Betrieb · Verlauf, darunter eine Liste wie
 * in Apple Mail (neueste oben, Tagestrenner), die geoeffnete Nachricht und unten das Antwort-Fenster.
 *
 *  - Handy: Liste -> Tippen -> Vollansicht mit Zurueck-Pfeil. Ab 900 px Breite zweispaltig
 *    (Liste links, Nachricht rechts).
 *  - Gast = echtes Gespraech mit dem Gast (eingehend + Antworten an die Gastadresse, WhatsApp mit
 *    Kennzeichen); Betrieb = Hinweise an den Betrieb, je Hinweis einmal (der SERVER teilt ein und fasst
 *    zusammen, `art`/`empfaenger`); Verlauf = die Ereignisse (Eckdaten geaendert, Angebot gesendet …).
 *  - Antworten nur im Gast-Reiter.
 */
import { useMemo, useState } from "react";
import { Icon, Segmente } from "../components";
import { useNarrowViewport } from "../../hooks/useNarrowViewport";
import { dekodiereMailBetreff } from "../../lib/mail-anzeige";
import { sendezeitpunkt } from "../../lib/gespraech";
import type { AuftragVerlaufEintrag } from "../../lib/auftrag-verlauf";
import {
  tagesBezeichnung,
  tagesSchluessel,
  teileInReiter,
  unbeantworteteNachrichtId,
  unbeantwortetText,
  type MailFensterReiter,
} from "../../lib/gespraech-mailfenster";
import { GespraechAntwort, useGespraechAntwort } from "./GespraechAntwort";
import { MailKurzZeile, MailListenZeile, MailNachricht, type MailKontext } from "./GespraechMailNachricht";
import { SYNTHETISCHE_NACHRICHT_ID, type GespraechEmailRow } from "./gespraech-typen";

function Tagestrenner({ zeitpunkt, jetzt }: { zeitpunkt: string; jetzt: number }) {
  return (
    <div className="border-b border-outline-variant/25 bg-surface-container-high/50 px-3.5 py-1.5 font-body-sm text-[12px] font-semibold text-secondary dark:border-outline/15 dark:bg-white/[0.03] dark:text-secondary-fixed-dim">
      {tagesBezeichnung(zeitpunkt, jetzt)}
    </div>
  );
}

function VerlaufListe({ eintraege, jetzt }: { eintraege: AuftragVerlaufEintrag[]; jetzt: number }) {
  if (eintraege.length === 0) {
    return (
      <p className="p-4 text-center font-body-sm text-[13px] text-secondary dark:text-secondary-fixed-dim">Noch keine Ereignisse.</p>
    );
  }
  return (
    <div data-testid="gespraech-verlauf">
      {eintraege.map((e, i) => {
        const vorher = eintraege[i - 1];
        const neuerTag = !vorher || tagesSchluessel(vorher.zeitpunkt) !== tagesSchluessel(e.zeitpunkt);
        return (
          <div key={e.key}>
            {neuerTag && <Tagestrenner zeitpunkt={e.zeitpunkt} jetzt={jetzt} />}
            <div className="flex items-start gap-3 border-b border-outline-variant/25 px-3.5 py-2.5 dark:border-outline/15">
              <Icon name={e.icon} className="mt-0.5 text-[18px] text-secondary dark:text-secondary-fixed-dim" />
              <span className="min-w-0 flex-1 font-body-sm text-[14px] text-on-background dark:text-inverse-on-surface">
                {e.titel}
                {e.detail ? <span className="text-secondary dark:text-secondary-fixed-dim"> · {e.detail}</span> : null}
              </span>
              <span className="shrink-0 font-body-sm text-[12.5px] text-secondary dark:text-secondary-fixed-dim">
                {new Date(e.zeitpunkt).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export interface GespraechMailfensterProps {
  eventId: string;
  /** Alle Nachrichten (inkl. der synthetischen Gast-Nachricht, falls es keine echte Mail gibt). */
  nachrichten: GespraechEmailRow[];
  ladend: boolean;
  /** Ereignisse fuer den Reiter „Verlauf“ (ohne `mail`/`version` — die stehen als Nachricht). */
  verlaufEintraege: AuftragVerlaufEintrag[];
  kundenName: string | null;
  kundenFirma: string | null;
  firmaName: string | null;
  /** E-Mail des Kunden fuer die „An“-Zeile; ohne Angabe die Absenderadresse der Gast-Nachricht. */
  kundenEmail?: string | null;
  /** „Silvester Gala-Dinner · 31.12.2026 · V-1252“ — Unterzeile unter dem Betreff. */
  vorgangInfo?: string | null;
  onGesendet?: () => void;
  onZuordnungGeaendert?: () => void;
}

export function GespraechMailfenster({
  eventId,
  nachrichten,
  ladend,
  verlaufEintraege,
  kundenName,
  kundenFirma,
  firmaName,
  kundenEmail,
  vorgangInfo,
  onGesendet,
  onZuordnungGeaendert,
}: GespraechMailfensterProps) {
  const zweispaltig = useNarrowViewport("(min-width: 900px)");
  const [reiter, setReiter] = useState<MailFensterReiter>("gast");
  const [ausgewaehltId, setAusgewaehltId] = useState<string | null>(null);
  const [zusatzOffen, setZusatzOffen] = useState<ReadonlySet<string>>(new Set());
  const [detailOffen, setDetailOffen] = useState(false);
  const [nurMitAnhang, setNurMitAnhang] = useState(false);
  const [antwortAufGewaehlt, setAntwortAufGewaehlt] = useState<string | null>(null);
  const jetzt = Date.now();

  const kontext: MailKontext = { kundenName, kundenFirma, firmaName, eventId };
  const { gast, betrieb } = useMemo(() => teileInReiter(nachrichten), [nachrichten]);
  const verlaufNeuesteZuerst = useMemo(() => [...verlaufEintraege].sort((a, b) => b.zeitpunkt.localeCompare(a.zeitpunkt)), [verlaufEintraege]);

  const aktuelleListe = reiter === "gast" ? gast : reiter === "betrieb" ? betrieb : [];
  const sichtbar = nurMitAnhang ? aktuelleListe.filter((m) => (m.attachmentsManifest?.length ?? 0) > 0) : aktuelleListe;
  // Auswahl: ausdruecklich gewaehlt, sonst am Desktop die neueste Nachricht (am Handy bleibt die Liste stehen).
  const gewaehlt = sichtbar.find((m) => m.id === ausgewaehltId) ?? (zweispaltig ? sichtbar[0] : undefined) ?? null;
  const unbeantwortetId = unbeantworteteNachrichtId(gast);
  const unbeantwortet = unbeantwortetId ? gast.find((m) => m.id === unbeantwortetId) : undefined;
  const unbeantwortetAnzeige = unbeantwortet ? unbeantwortetText(sendezeitpunkt(unbeantwortet), jetzt) : null;

  // Antwortziel: die gewaehlte eingehende Gast-Nachricht, sonst die neueste echte eingehende.
  const echteEingehende = gast.filter((m) => m.direction === "inbound" && m.id !== SYNTHETISCHE_NACHRICHT_ID && m.channel !== "whatsapp");
  const antwortAufId =
    antwortAufGewaehlt && echteEingehende.some((m) => m.id === antwortAufGewaehlt)
      ? antwortAufGewaehlt
      : gewaehlt && echteEingehende.some((m) => m.id === gewaehlt.id)
        ? gewaehlt.id
        : (echteEingehende[0]?.id ?? null);
  const antwortAuf = antwortAufId ? gast.find((m) => m.id === antwortAufId) : undefined;
  const standardBetreff = dekodiereMailBetreff(antwortAuf?.subject ?? gewaehlt?.subject ?? gast[0]?.subject);
  const antwort = useGespraechAntwort({ eventId, antwortAufId, standardBetreff, onGesendet });

  const letzteAusgehende = gast.find((m) => m.direction === "outbound");
  const kundenAdresse = kundenEmail ?? echteEingehende[0]?.fromAddr ?? null;

  function waehleReiter(r: MailFensterReiter) {
    setReiter(r);
    setAusgewaehltId(null);
    setZusatzOffen(new Set());
    setDetailOffen(false);
  }
  function oeffne(id: string) {
    setAusgewaehltId(id);
    setZusatzOffen(new Set());
    setDetailOffen(true);
    const m = nachrichten.find((x) => x.id === id);
    if (m?.direction === "inbound") setAntwortAufGewaehlt(id);
  }
  function aufklappen(id: string) {
    setZusatzOffen((s) => new Set(s).add(id));
  }
  function zuklappen(id: string) {
    setZusatzOffen((s) => {
      const n = new Set(s);
      n.delete(id);
      return n;
    });
  }
  function antwortenAuf(id: string) {
    setAntwortAufGewaehlt(id);
    // Das Feld erscheint ggf. erst nach dem naechsten Render (Handy: Vollansicht) — dann fokussieren.
    setTimeout(() => antwort.fokussieren(), 0);
  }
  async function kiEntwurfFuer(id: string) {
    setAntwortAufGewaehlt(id);
    await antwort.entwurfLaden();
    antwort.fokussieren();
  }

  const reiterWerte = [
    { key: "gast" as const, label: `Gast · ${gast.length}` },
    { key: "betrieb" as const, label: `Betrieb · ${betrieb.length}` },
    { key: "verlauf" as const, label: "Verlauf" },
  ];

  // ---- Listenspalte -------------------------------------------------------------------------
  const liste =
    ladend ? (
      <div className="space-y-3 p-3.5">
        {[0, 1].map((i) => (
          <div key={i} className="h-16 animate-pulse rounded-2xl bg-surface-container-high dark:bg-white/[0.04]" />
        ))}
      </div>
    ) : sichtbar.length === 0 ? (
      <p className="p-4 text-center font-body-sm text-[13px] text-secondary dark:text-secondary-fixed-dim">
        {nurMitAnhang ? "Kein Eintrag passt zu diesem Filter." : reiter === "betrieb" ? "Noch keine Hinweise an den Betrieb." : "Noch keine Nachrichten."}
      </p>
    ) : (
      <div data-testid="gespraech-liste">
        {sichtbar.map((m, i) => {
          const zeit = sendezeitpunkt(m);
          const vorher = sichtbar[i - 1];
          const neuerTag = !vorher || tagesSchluessel(sendezeitpunkt(vorher)) !== tagesSchluessel(zeit);
          return (
            <div key={m.id}>
              {neuerTag && <Tagestrenner zeitpunkt={zeit} jetzt={jetzt} />}
              <MailListenZeile
                email={m}
                kontext={kontext}
                ausgewaehlt={gewaehlt?.id === m.id && (zweispaltig || detailOffen)}
                unbeantwortet={reiter === "gast" && m.id === unbeantwortetId ? unbeantwortetAnzeige : null}
                onOeffnen={() => oeffne(m.id)}
              />
            </div>
          );
        })}
      </div>
    );

  const listenSpalte = (
    <div className={zweispaltig ? "border-r border-outline-variant/40 dark:border-outline/25" : ""}>
      <div className="flex items-center justify-end border-b border-outline-variant/25 px-3.5 py-1.5 dark:border-outline/15">
        <button
          type="button"
          data-testid="gespraech-filter-anhaenge"
          aria-pressed={nurMitAnhang}
          onClick={() => setNurMitAnhang((v) => !v)}
          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 font-body-sm text-[12px] font-semibold transition-colors ${
            nurMitAnhang ? "bg-primary/[0.1] text-primary" : "border border-outline-variant/50 text-secondary hover:text-on-background dark:border-outline/30 dark:text-secondary-fixed-dim dark:hover:text-inverse-on-surface"
          }`}
        >
          <Icon name="attach_file" className="text-[14px]" />
          Mit Anhängen
        </button>
      </div>
      {liste}
    </div>
  );

  // ---- Detailspalte -------------------------------------------------------------------------
  const betreffTitel = gewaehlt ? dekodiereMailBetreff(gewaehlt.subject)?.trim() : null;
  const darfAntworten = reiter === "gast";
  const detailSpalte = (
    <div className={zweispaltig ? "bg-surface-container-lowest/60 dark:bg-white/[0.015]" : ""}>
      {!zweispaltig && (
        <div className="flex items-center gap-2 border-b border-outline-variant/25 px-3 py-2 dark:border-outline/15">
          <button
            type="button"
            data-testid="gespraech-zurueck"
            aria-label="Zurück zur Liste"
            onClick={() => setDetailOffen(false)}
            className="flex items-center gap-1 rounded-md px-1 py-0.5 font-body-sm text-[14px] font-semibold text-primary hover:underline"
          >
            <Icon name="arrow_back_ios_new" className="text-[16px]" />
            Gespräch
          </button>
        </div>
      )}
      {gewaehlt && (
        <div className="border-b border-outline-variant/25 px-3.5 pb-2.5 pt-3.5 dark:border-outline/15">
          <h4 className="font-body-sm text-[19px] font-extrabold leading-[1.3] text-on-background dark:text-inverse-on-surface">{betreffTitel || "(Ohne Betreff)"}</h4>
          {(kundenName || vorgangInfo) && (
            <p className="mt-0.5 font-body-sm text-[13px] text-secondary dark:text-secondary-fixed-dim">{[kundenName?.trim(), vorgangInfo].filter(Boolean).join(" · ")}</p>
          )}
        </div>
      )}
      {sichtbar.map((m) => {
        const offen = m.id === gewaehlt?.id || zusatzOffen.has(m.id);
        return offen ? (
          <MailNachricht
            key={m.id}
            email={m}
            kontext={kontext}
            unbeantwortet={reiter === "gast" && m.id === unbeantwortetId ? unbeantwortetAnzeige : null}
            entwurfLaeuft={antwort.entwurfLaeuft}
            onAntworten={darfAntworten && m.direction === "inbound" ? () => antwortenAuf(m.id) : undefined}
            onKiEntwurf={darfAntworten && m.direction === "inbound" ? () => void kiEntwurfFuer(m.id) : undefined}
            onZuordnungGeaendert={() => onZuordnungGeaendert?.()}
            onZuklappen={m.id !== gewaehlt?.id ? () => zuklappen(m.id) : undefined}
          />
        ) : (
          <MailKurzZeile key={m.id} email={m} kontext={kontext} onAufklappen={() => aufklappen(m.id)} />
        );
      })}
      {darfAntworten && (
        <GespraechAntwort
          z={antwort}
          kundenName={kundenName}
          kundenEmail={kundenAdresse}
          vonAnzeige={letzteAusgehende?.fromAddr ?? firmaName}
        />
      )}
    </div>
  );

  return (
    <div data-testid="gespraech-mailfenster" data-layout={zweispaltig ? "zweispaltig" : "einspaltig"} className="overflow-hidden rounded-xl border border-outline-variant/40 dark:border-outline/25">
      <div className="border-b border-outline-variant/25 p-2.5 dark:border-outline/15">
        <Segmente werte={reiterWerte} wert={reiter} onWahl={waehleReiter} label="Gespräch: Gast, Betrieb oder Verlauf" />
      </div>
      {reiter === "verlauf" ? (
        <VerlaufListe eintraege={verlaufNeuesteZuerst} jetzt={jetzt} />
      ) : zweispaltig ? (
        <div className="grid grid-cols-[minmax(260px,360px)_minmax(0,1fr)]">
          {listenSpalte}
          {detailSpalte}
        </div>
      ) : detailOffen ? (
        detailSpalte
      ) : (
        <>
          {listenSpalte}
          {/* Ohne Nachricht gibt es nichts zu antworten-auf-tippen: das Antwortfenster steht trotzdem bereit. */}
          {darfAntworten && sichtbar.length === 0 && !ladend && (
            <GespraechAntwort z={antwort} kundenName={kundenName} kundenEmail={kundenAdresse} vonAnzeige={letzteAusgehende?.fromAddr ?? firmaName} />
          )}
        </>
      )}
    </div>
  );
}
